/**
 * BigNumber - Hero metric display
 *
 * Usage:
 *   <BigNumber
 *     data={{
 *       value: 1234567,
 *       label: 'Total Revenue',
 *       prefix: '$',
 *       trend: 15.2,
 *       comparison: 'vs last month'
 *     }}
 *   />
 */

import type { BlockProps } from './types';
import { getTrendColor } from './colors';
import './blocks.css';

export interface BigNumberData {
  /** The main value */
  value: number | string;
  /** Label describing the metric */
  label?: string;
  /** Prefix (e.g., '$', '€') */
  prefix?: string;
  /** Suffix (e.g., '%', 'users') */
  suffix?: string;
  /** Trend percentage */
  trend?: number;
  /** Comparison text */
  comparison?: string;
  /** Previous value for context */
  previousValue?: number | string;
}

export interface BigNumberProps extends BlockProps<BigNumberData> {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Center align */
  centered?: boolean;
}

const sizeStyles = {
  sm: { value: '2rem', label: '12px', trend: '12px' },
  md: { value: '2.5rem', label: '13px', trend: '13px' },
  lg: { value: '3.5rem', label: '14px', trend: '14px' },
  xl: { value: '4.5rem', label: '15px', trend: '15px' },
};

export function BigNumber({
  data,
  title,
  size = 'lg',
  centered = false,
  className,
}: BigNumberProps) {
  const { value: rawValue, label, prefix, suffix, trend, comparison, previousValue } = data;

  // Guard against undefined/null values from LLM-generated card data
  const value = rawValue ?? 0;
  const styles = sizeStyles[size];

  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(1)}B`;
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  return (
    <div
      className={`big-number ${className || ''}`}
      style={{
        padding: 'var(--space-4)',
        textAlign: centered ? 'center' : 'left',
      }}
    >
      {title && (
        <div style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 'var(--space-2)',
        }}>
          {title}
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '4px',
        flexWrap: 'wrap',
        justifyContent: centered ? 'center' : 'flex-start',
      }}>
        {prefix && (
          <span style={{
            fontSize: `calc(${styles.value} * 0.6)`,
            fontWeight: 500,
            color: 'var(--color-text-muted)',
          }}>
            {prefix}
          </span>
        )}
        <span style={{
          fontSize: styles.value,
          fontWeight: 700,
          color: 'var(--color-text)',
          lineHeight: 1.1,
        }}>
          {formatValue(value)}
        </span>
        {suffix && (
          <span style={{
            fontSize: `calc(${styles.value} * 0.4)`,
            fontWeight: 500,
            color: 'var(--color-text-muted)',
          }}>
            {suffix}
          </span>
        )}
      </div>

      {label && (
        <div style={{
          fontSize: styles.label,
          color: 'var(--color-text-subtle)',
          marginTop: 'var(--space-1)',
        }}>
          {label}
        </div>
      )}

      {(trend !== undefined || previousValue !== undefined) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: 'var(--space-2)',
          justifyContent: centered ? 'center' : 'flex-start',
        }}>
          {trend !== undefined && (
            <span style={{
              fontSize: styles.trend,
              fontWeight: 600,
              color: getTrendColor(trend),
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}>
              {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'}
              {Math.abs(trend).toFixed(1)}%
            </span>
          )}
          {comparison && (
            <span style={{
              fontSize: styles.trend,
              color: 'var(--color-text-muted)',
            }}>
              {comparison}
            </span>
          )}
          {previousValue !== undefined && !comparison && (
            <span style={{
              fontSize: styles.trend,
              color: 'var(--color-text-muted)',
            }}>
              (was {formatValue(previousValue)})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default BigNumber;
